// resources/js/Pages/Formula/TreeNode.js - UPDATED FOR UI ELEMENTS
import React from 'react';

export default function TreeNode({ node, depth = 0, onSelect, selectedId }) {
  const isSelected = node.id === selectedId;
  const isRoot = node.key === 'Root';

  // ✅ NEW: Get field type info
  const getFieldTypeInfo = (valueType) => {
    switch (valueType) {
      case 'score':
        return { label: 'score', color: 'success' };
      case 'importance':
        return { label: 'importance', color: 'warning' };
      case 'badge':
        return { label: 'badge', color: 'secondary' };
      case 'scene_number':
        return { label: 'scene', color: 'warning' };
      case 'scene_list':
        return { label: 'scenes', color: 'warning' };
      case 'questions_for_llm':
        return { label: 'questions', color: 'info' };
      case 'string':
      default:
        return { label: 'text', color: 'info' };
    }
  };

  const getNodeDisplay = () => {
    if (isRoot) {
      return (
        <>
          🏠 <strong>{node.key}</strong>  
          <span className="ms-2 badge bg-primary">object</span>  
        </>
      );
    } else if (node.type === 'list' || node.type === 'array') {
      // ✅ REVISED LOGIC FOR LISTS/ARRAYS
      // Check for our special behavioral type first
      if (node.renderAs === 'llm_questions') {
        return (
          <>
            🤖 <strong>{node.key}</strong>
            <span className="ms-2 badge bg-primary">AI Questions</span>
          </>
        )
      }
      // Fallback to normal array display
      return (
        <>
          📋 <strong>{node.key}</strong>
          <span className="ms-2 badge bg-secondary">array</span>  
        </>
      )
    } else if (node.type === 'text-block') {
      // ✅ NEW: Text block display
      const hasContent = node.content && node.content.trim();
      return (
        <>
          📝 {hasContent ? 'Static Text' : 'Empty Text Block'}
          <span className="ms-2 badge bg-success">text-block</span>
          {hasContent && (
            <small className="d-block text-muted mt-1" style={{ fontSize: '0.75em' }}>
              {node.content.length > 40 
                ? node.content.substring(0, 40) + '...' 
                : node.content}
            </small>
          )}
        </>
      );
    } else if (node.type === 'section') {
      // ✅ NEW: Section display
      const title = node.title || 'Section';
      const childCount = node.children ? node.children.length : 0;
      return (
        <>
          📦 <strong>{title}</strong>
          <span className="ms-2 badge bg-dark">section</span>
          <small className="d-block text-muted mt-1" style={{ fontSize: '0.75em' }}>
            Contains {childCount} item{childCount !== 1 ? 's' : ''}
          </small>
        </>
      );
    } else if (node.type === 'divider') {
      // ✅ NEW: Divider display
      return (
        <>
          ➖ Divider Line
          <span className="ms-2 badge bg-secondary">divider</span>
        </>
      );
    } else {
      // Field node - ✅ UPDATED: Show field type
      const hasInstruction = node.instruction && node.instruction.trim();
      const fieldTypeInfo = getFieldTypeInfo(node.valueType);
      const styleColors = {
        warning: '#FF9800', danger: '#f44336', info: '#2196F3',
        success: '#4CAF50', action: '#9C27B0', neutral: '#78909C'
      };
      const styleIcons = {
        warning: '⚠️', danger: '🚨', info: 'ℹ️',
        success: '✅', action: '🎯', neutral: '📌'
      };

      return (
        <>
          📝 {node.key}
          <span className="ms-2 badge bg-info">
            field
          </span>
          {/* ✅ Show field type badge */}
          <span className={`ms-1 badge bg-${fieldTypeInfo.color}`} style={{ fontSize: '0.65em' }}>
            {fieldTypeInfo.label}
          </span>
          {/* ✅ Show style preset indicator */}
          {node.style && styleColors[node.style] && (
            <span className="ms-1" style={{ fontSize: '0.7em' }} title={`Style: ${node.style}`}>
              {styleIcons[node.style]}
            </span>
          )}
          {hasInstruction && (
            <small className="d-block text-muted mt-1" style={{ fontSize: '0.75em' }}>
              {node.instruction.length > 40
                ? node.instruction.substring(0, 40) + '...'
                : node.instruction}
            </small>
          )}
        </>
      );
    }
  };

  // Create indentation string
  const getIndentationString = () => {
    if (depth === 0) return '';
    
    let indentStr = '';
    for (let i = 0; i < depth; i++) {
      if (i === depth - 1) {
        indentStr += '├─ ';
      } else {
        indentStr += '│  ';
      }
    }
    return indentStr;
  };

  return (
    <>
      <div
        className={`py-2 px-3 cursor-pointer tree-node-item d-flex align-items-start ${
          isSelected ? 'selected' : ''
        }`}
        onClick={() => onSelect(node)}
        style={{
          backgroundColor: depth > 0 ? `rgba(248, 249, 250, ${Math.min(depth * 0.4, 0.9)})` : 'white',
          borderLeft: depth > 0 ? `${depth * 2}px solid rgba(13, 202, 240, ${Math.min(depth * 0.3, 0.7)})` : 'none'
        }}
      >
        {/* Indentation with tree characters */}
        {depth > 0 && (
          <span 
            className="text-muted me-2 flex-shrink-0" 
            style={{ 
              fontSize: '0.85em',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              color: '#6c757d',
              lineHeight: '1.2',
              whiteSpace: 'pre'
            }}
          >
            {getIndentationString()}
          </span>
        )}
        
        {/* Node content */}
        <div className="flex-grow-1">
          {getNodeDisplay()}
        </div>
      </div>

      {/* Render children */}
      {node.children?.map((child) => (
        <TreeNode
          key={child.id}
          node={child}
          depth={depth + 1}
          onSelect={onSelect}
          selectedId={selectedId}
        />
      ))}
    </>
  );
}